import { useState, useEffect } from 'react'

interface LoadingScreenProps {
  onLoadComplete: () => void
}

export function LoadingScreen({ onLoadComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0)
  const [loadingText, setLoadingText] = useState('正在准备圣诞惊喜...')
  
  useEffect(() => {
    // 模拟加载进度
    const loadingSteps = [
      { progress: 20, text: '🎄 加载圣诞树...' },
      { progress: 40, text: '✨ 准备装饰品...' },
      { progress: 60, text: '📷 加载照片...' },
      { progress: 80, text: '🎵 准备音乐...' },
      { progress: 100, text: '🎁 准备完成！' },
    ]
    
    let currentStep = 0
    
    const interval = setInterval(() => {
      if (currentStep < loadingSteps.length) {
        setProgress(loadingSteps[currentStep].progress)
        setLoadingText(loadingSteps[currentStep].text)
        currentStep++
      } else {
        clearInterval(interval)
        // 延迟一下再完成，让用户看到100%
        setTimeout(() => {
          onLoadComplete()
        }, 500)
      }
    }, 600)
    
    return () => clearInterval(interval)
  }, [onLoadComplete])
  
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-[#0a1628] via-[#0d1f3c] to-[#0a0a1a]">
      {/* 背景星星效果 */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              opacity: Math.random() * 0.8 + 0.2,
            }}
          />
        ))}
      </div>
      
      {/* 主要内容 */}
      <div className="relative z-10 text-center px-8">
        {/* 圣诞树图标动画 */}
        <div className="mb-8 relative">
          <div className="text-8xl animate-bounce" style={{ animationDuration: '2s' }}>
            🎄
          </div>
          {/* 金色光晕 */}
          <div
            className="absolute inset-0 blur-3xl opacity-30"
            style={{
              background: 'radial-gradient(circle, #FFD700 0%, transparent 70%)',
            }}
          />
        </div>
        
        {/* 标题 */}
        <h1
          className="text-4xl md:text-5xl font-serif mb-4"
          style={{
            color: '#FFD700',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.4)',
          }}
        >
          ✨ Merry Christmas ✨
        </h1>
        
        <p className="text-white/70 text-lg mb-8">
          献给最特别的你
        </p>
        
        {/* 加载进度条 */}
        <div className="w-64 md:w-80 mx-auto mb-4">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)',
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
              }}
            />
          </div>
        </div>
        
        {/* 加载文字 */}
        <p className="text-white/60 text-sm">
          {loadingText}
        </p>
        
        {/* 进度百分比 */}
        <p
          className="text-2xl font-bold mt-4"
          style={{
            color: '#FFD700',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
          }}
        >
          {progress}%
        </p>
      </div>
      
      {/* 底部装饰 */}
      <div className="absolute bottom-8 text-white/30 text-sm">
        Made with ❤️ for you
      </div>
      
      {/* 雪花效果 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <div
            key={`snow-${i}`}
            className="absolute text-white/40 animate-fall"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-20px`,
              fontSize: `${Math.random() * 10 + 10}px`,
              animationDuration: `${Math.random() * 3 + 4}s`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          >
            ❄
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0.3;
          }
        }
        .animate-fall {
          animation: fall linear infinite;
        }
      `}</style>
    </div>
  )
}